import { useClientsInfiniteQuery, useInView } from "creso-core/hooks";
import { cn, ScrollArea, V2Loader } from "web-design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import { Client } from "creso-core/types";
import { useDebounceValue } from "usehooks-ts";
import { TRANSACTION_CLIENT_STATES } from "creso-core/constants";
import ClientAvatar from "./client-avatar";
import SearchBar from "./search-bar";

export interface Props {
  onSelect: (client: Client) => void;
}

export default function ChooseClient(props: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState("");

  const inView = useInView(scrollerRef);

  const [debouncedSearch] = useDebounceValue(search, 300);

  const {
    clients = [],
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useClientsInfiniteQuery(debouncedSearch, {
    status: TRANSACTION_CLIENT_STATES,
    has_verified_bank_account: true,
  });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const { onSelect } = props;

  const body = useMemo(() => {
    return (
      <V2Loader size="large" className="size-full" isLoading={isFetching}>
        <ul
          className={cn(
            search
              ? "flex flex-col gap-px w-full"
              : "grid grid-cols-5 gap-3 w-full"
          )}
        >
          {clients.map((d) => {
            let isActive = false;
            if (d.last_seen) {
              const lastSeenDate = new Date(d.last_seen);
              isActive = Date.now() - lastSeenDate.getTime() < 2 * 60 * 1000;
            }
            return (
              <li key={d.id} role="button" onClick={() => onSelect(d)}>
                <ClientAvatar
                  name={d.name}
                  holdingPattern={d.holding_pattern}
                  compact={!!search}
                  subtitle={d.phone_number}
                  isActive={isActive}
                />
              </li>
            );
          })}
          {hasNextPage && (
            <li>
              <div className="w-full h-4" ref={scrollerRef}></div>
            </li>
          )}
        </ul>
        {clients.length === 0 && (
          <div className="flex justify-center items-center h-full w-full">
            <span className="text-sm text-muted-foreground">
              No clients found
            </span>
          </div>
        )}
      </V2Loader>
    );
  }, [isFetching, clients, search, onSelect, hasNextPage]);

  return (
    <ScrollArea className="flex-1 size-full w-240 p-4">
      <div className="flex flex-col">
        <SearchBar onChange={(e) => setSearch(e.target.value)} />
        {body}
      </div>
    </ScrollArea>
  );
}
